import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { focusElement } from '../../utils/useFocusOnOpen';
import { displayName } from '../../utils/userLabel';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import PhotoGallery from '../PhotoGallery';
import ProvenanceGraph from '../specimens/ProvenanceGraph';
import PinMap from './PinMap';
import BankTab from './tabs/BankTab';
import DnaTab from './tabs/DnaTab';
import TaxonomyTab from './tabs/TaxonomyTab';
import CultureMicrographs from './CultureMicrographs';
import GrowthPanel from '../culture/GrowthPanel';
import { can } from '../../utils/permissions';
import { Code, Day, PlatePill } from './cells';
import { defaultPlateId } from './quickActions';
import { STAGES, canDo } from './stages';
import * as S from '../specimens/styles';

/*
 * A stage of an isolate, opened in place: pressing a stage node on an
 * isolate's pipeline card (IsolatePipelines) or a cell of the table's
 * pipeline strip opens this dialog on that stage (IsolateHub's ?peek=, with
 * ?hl= naming the isolate), instead of only highlighting the row.
 *
 *   collection      the origin record (GET /collections/{id} or
 *                   /external/{id}), the identifications logged on it, its
 *                   field photographs, and a pin on a map
 *   culture         the plates, each check logged on them (the culture
 *                   history), the subculture lineage (the specimen's own
 *                   workflow graph, read-only), the dish photographs and the
 *                   micrographs (CultureMicrographs: GET /microscopy for each
 *                   plate, the origin record and the strain, grouped by what
 *                   they show), with Upload dish photo / Upload micrograph
 *                   (the labPhoto and micrograph quick actions, on a plate)
 *   dna             the DNA tab: each extract as recorded, its runs and
 *                   PITAX answers, and the reference phylogeny opened on
 *                   the isolate's genus (DnaTab, IsolatePhylogeny)
 *   identification  the taxonomy tab
 *   bank            the bank tab: tubes, places, preservation
 *
 * It reads the isolate's dossier (useDossiers, the same copy the row's tabs
 * use) and asks the server only for what the dossier does not carry: the
 * origin record, the graph, and (in DnaTab) the reference tree. A redacted
 * isolate (a student's, seen through an assignment) shows no record, no
 * coordinates.
 * The row's own buttons stay where they are: "Open in the table" highlights
 * and expands the row.
 */
export const PEEKS = STAGES;

export default function IsolatePeek({
  stage, item, entry, user, t, onClose, onOpenRow, onAction, onNavigate, onStorageSaved, onRefresh, onRetry,
}) {
  const headingRef = useRef(null);
  const open = !!stage && PEEKS.includes(stage);

  useEffect(() => {
    if (!open) return undefined;
    focusElement(headingRef.current);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, stage, item?.specimen_id, onClose]);

  if (!open) return null;
  const dossier = entry?.data || null;
  const specimen = dossier?.specimen || null;
  const code = item?.specimen_code || specimen?.specimen_code || '';
  const labCode = item?.lab_code || specimen?.lab_code || '';
  const taxon = item?.taxon_name || dossier?.latest?.identification?.taxon_name || '';
  const props = { item, dossier, user, t, onAction, onNavigate, onStorageSaved, onRefresh };

  const panel = () => {
    if (!dossier || !item) return null;
    switch (stage) {
      case 'collection': return <CollectionPeek {...props} />;
      case 'culture': return <CulturePeek {...props} />;
      case 'dna': return <DnaPeek {...props} />;
      case 'identification': return <TaxonomyTab {...props} />;
      case 'bank': return <BankTab {...props} />;
      default: return null;
    }
  };

  return (
    <div className="hub-drawer-backdrop" onClick={onClose} data-testid="hub-peek-backdrop">
      <aside className="hub-drawer hub-drawer--wide" role="dialog" aria-modal="true" aria-labelledby="hub-peek-title"
        onClick={(e) => e.stopPropagation()} data-testid="hub-peek" data-stage={stage}>
        <div className="hub-drawer__head">
          <h3 id="hub-peek-title" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 17 }}>
            {t(`peek.title.${stage}`)}
          </h3>
          <button type="button" onClick={onClose} title={t('peek.close')} aria-label={t('peek.close')}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--c-text-muted-fg)', lineHeight: 1 }}>
            ×
          </button>
        </div>
        <p style={{ ...S.hint, margin: '4px 0 10px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {code && <Code strong>{code}</Code>}
          {labCode && <Code>{labCode}</Code>}
          {taxon && <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{taxon}</bdi>}
          {item && onOpenRow && (
            <button type="button" style={S.ghostBtn} onClick={() => onOpenRow(item.specimen_id)} data-testid="hub-peek-open-row">
              {t('peek.openRow')}
            </button>
          )}
        </p>
        {entry?.loading && !dossier && (
          <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />
        )}
        {entry?.error && !dossier && (
          <div role="alert" style={S.error}>
            {entry.error}{' '}
            {onRetry && <button type="button" style={S.ghostBtn} onClick={onRetry}>{t('retry')}</button>}
          </div>
        )}
        {dossier && !item && <p style={S.hint}>{t('peek.notOnPage')}</p>}
        {dossier?.restricted && <p role="note" style={{ ...S.hint, marginTop: 0 }}>{t('details.restricted')}</p>}
        <div key={`${stage}-${item?.specimen_id}`}>{panel()}</div>
      </aside>
    </div>
  );
}

/* ── collection ─────────────────────────────────────────────────────────── */
const COLLECTION_FIELDS = [
  ['code', (r) => r.qr_uid],
  ['taxon', (r) => [r.current_taxon_name || r.taxon_name, r.current_rank || r.taxonomic_rank, r.current_confidence || r.confidence]
    .filter(Boolean).join(' · ')],
  ['collected', (r) => r.collection_datetime || r.original_collection_date, 'day'],
  ['place', (r) => r.location_name || r.original_location_name || r.locality_text],
  ['region', (r) => r.region],
  ['coordinates', (r) => (r.gps_lat != null && r.gps_lng != null ? `${r.gps_lat}, ${r.gps_lng}` : null), 'ltr'],
  ['altitude', (r) => r.altitude_m ?? r.original_altitude_m],
  ['substrate', (r) => r.substrate],
  ['host', (r) => r.host_species],
  ['habitat', (r) => r.habitat_biome],
  ['microenvironment', (r) => r.microenvironment],
  ['size', (r) => [r.fruiting_body_size, r.fruiting_body_size_estimated ? '≈' : ''].filter(Boolean).join(' ')],
  ['count', (r) => r.fruiting_body_count],
  ['growth', (r) => r.growth_pattern],
  ['stage', (r) => r.phenological_stage],
  ['colourChange', (r) => (r.colour_change === true ? 'yes' : r.colour_change === false ? 'no' : null), 'yesno'],
  // A field collection's collector, as GET /collections/{id} names it: the
  // name it was recorded under (an imported archive's own collector,
  // migration 0069), else the account that sent it.
  ['collector', (r) => r.observer],
  ['donor', (r) => r.donor_name],
  ['received', (r) => r.intake_datetime || r.received_date, 'day'],
  // An intake's: who first collected it. A collection's is its observer
  // already, so it is not said twice.
  ['originalCollector', (r) => (r.observer ? null : r.original_collector)],
  ['labStatus', (r) => r.lab_status],
  ['notes', (r) => r.general_notes],
];

const humanize = (value) => (typeof value === 'string' ? value.replace(/_/g, ' ') : value);

export function CollectionPeek({ item, dossier, t }) {
  const [record, setRecord] = useState(null);
  const [state, setState] = useState(item.redacted ? 'hidden' : 'loading');
  const path = item.sample_id ? `/collections/${item.sample_id}` : item.external_id ? `/external/${item.external_id}` : null;

  useEffect(() => {
    if (item.redacted || !path) { setState(item.redacted ? 'hidden' : 'none'); return undefined; }
    let alive = true;
    setState('loading');
    api.get(path)
      .then((res) => { if (alive) { setRecord(res?.data || null); setState('ready'); } })
      .catch((e) => { if (alive) setState(e?.response?.status === 403 || e?.response?.status === 404 ? 'hidden' : `failed:${errorText(e)}`); });
    return () => { alive = false; };
  }, [path, item.redacted]);

  const history = dossier.identifications?.history || [];
  const photos = (dossier.photos || []).filter((p) => p.source === 'collection' || p.source === 'external');
  const specimen = dossier.specimen || {};
  const lat = record?.gps_lat ?? (item.redacted ? null : specimen.gps_lat);
  const lng = record?.gps_lng ?? (item.redacted ? null : specimen.gps_lng);
  const rows = record ? COLLECTION_FIELDS.map(([key, read, kind]) => [key, read(record), kind]).filter(([, v]) => v !== null && v !== undefined && v !== '') : [];

  return (
    <div data-testid="hub-peek-collection">
      <h4 style={h4}>{item.external_id ? t('peek.collection.intake') : t('peek.collection.metadata')}</h4>
      {state === 'loading' && <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />}
      {state === 'hidden' && <p style={S.hint}>{t('peek.collection.notVisible')}</p>}
      {state === 'none' && <p style={S.hint}>{t('peek.collection.noRecord')}</p>}
      {state.startsWith('failed:') && <p role="alert" style={S.error}>{t('peek.collection.loadFailed')} {state.slice(7)}</p>}
      {state === 'ready' && (
        <dl style={facts} data-testid="hub-peek-record">
          {rows.map(([key, value, kind]) => (
            <div key={key} style={{ display: 'contents' }}>
              <dt style={dt}>{t(`peek.fields.${key}`)}</dt>
              <dd style={dd}>
                {kind === 'day' ? <Day value={value} />
                  : kind === 'yesno' ? t(`peek.fields.${value}`)
                    : kind === 'ltr' ? <bdi dir="ltr">{value}</bdi>
                      : humanize(value)}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <h4 style={h4}>{t('peek.collection.observations')}</h4>
      {history.length === 0 ? <p style={S.hint}>{t('peek.collection.noObservations')}</p> : (
        <ul className="hub-records" data-testid="hub-peek-observations">
          {history.map((h) => (
            <li key={h.identification_id} className="hub-record">
              <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{h.taxon_name}</bdi>
              {' '}<span style={S.hint}>
                {[h.taxonomic_rank, h.confidence, h.identification_method].filter(Boolean).map(humanize).join(' · ')}
                {' · '}<Day value={h.identification_date} />
                {h.identified_by_name ? ` · ${displayName(h.identified_by_name, t)}` : ''}
                {h.expert_approval ? ` · ${t('peek.approved')}` : ''}
              </span>
              {h.notes && <div style={{ fontSize: 13 }}>{h.notes}</div>}
            </li>
          ))}
        </ul>
      )}

      <h4 style={h4}>{t('peek.collection.photos')}</h4>
      {photos.length === 0 ? <p style={S.hint}>{t('details.noPhotos')}</p>
        : <PhotoGallery photos={photos} thumbWidth={96} thumbHeight={72} testId="hub-peek-photos" />}

      <h4 style={h4}>{t('peek.collection.map')}</h4>
      {lat != null && lng != null
        ? <PinMap lat={lat} lng={lng} label={item.specimen_code} />
        : <p style={S.hint}>{t('peek.collection.noPlace')}</p>}
    </div>
  );
}

/* ── culture ────────────────────────────────────────────────────────────── */
export function CulturePeek({ item, dossier, user, t, onAction }) {
  const plates = useMemo(() => [...(dossier.plates || [])]
    .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || ''))), [dossier.plates]);
  const checks = dossier.checks || [];
  // A lab photo typed "microscopy" is a micrograph taken with a phone at the
  // eyepiece: listed with the micrographs, not among the dishes.
  const platePhotos = (dossier.photos || []).filter((p) => p.source === 'plate');
  const photos = platePhotos.filter((p) => p.photo_type !== 'microscopy');
  const eyepiece = platePhotos.filter((p) => p.photo_type === 'microscopy');
  // The quick forms offer only plates already made; the peek opens them on
  // the isolate's default one.
  const madePlates = plates.filter((p) => p.status !== 'awaiting_creation');
  const plateId = defaultPlateId(item, madePlates) || undefined;
  const canDish = canDo(user, 'labPhoto') && !!onAction;
  const canMicro = canDo(user, 'micrograph') && !!onAction;
  const [graph, setGraph] = useState(null);
  const [graphState, setGraphState] = useState('loading');
  const tSpec = (key, opts) => t(`specimens:${key}`, opts);

  useEffect(() => {
    if (item.redacted) { setGraphState('hidden'); return undefined; }
    let alive = true;
    api.get(`/specimens/${item.specimen_id}/graph`)
      .then((res) => { if (alive) { setGraph(res?.data || null); setGraphState('ready'); } })
      .catch(() => { if (alive) setGraphState('failed'); });
    return () => { alive = false; };
  }, [item.specimen_id, item.redacted]);

  const stageTitle = (stage) => (stage.title_is_default ? tSpec(`stage.types.${stage.stage_type}`) : stage.title);
  const generations = new Map(plates.map((p, i) => [p.plate_id, i + 1]));
  // Each plate with a dish photograph, and its newest: the Culture analysis page opens on it.
  const measurable = plates.map((plate) => {
    const own = photos.filter(ph => ph.record_id === plate.plate_id);
    return own.length ? { plate, photo: own[own.length - 1] } : null;
  }).filter(Boolean);

  return (
    <div data-testid="hub-peek-culture">
      <h4 style={h4}>{t('peek.culture.plates')}</h4>
      {plates.length === 0 && <p style={S.hint}>{t('details.noPlates')}</p>}
      <ul className="hub-records" data-testid="hub-peek-plates">
        {plates.map((p) => {
          const own = checks.filter((c) => c.plate_id === p.plate_id)
            .sort((a, b) => String(b.check_date || '').localeCompare(String(a.check_date || '')));
          return (
            <li key={p.plate_id} className="hub-record" data-testid={`hub-peek-plate-${p.plate_id}`}>
              <div className="hub-record__head" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Code strong>{p.plate_code}</Code>
                <PlatePill status={p.status} t={t} />
                <span style={S.hint}>{t('peek.culture.generation', { n: generations.get(p.plate_id) })}</span>
                {p.protocol && <span style={S.hint}>{p.protocol}{p.temperature_c != null ? ` · ${p.temperature_c}°C` : ''}</span>}
                {p.days_in_culture != null && <span style={S.hint}>{t('lab.days', { count: p.days_in_culture })}</span>}
                <span style={S.hint}><Day value={p.created_at} /></span>
              </div>
              {p.notes && <div style={{ fontSize: 13 }}>{p.notes}</div>}
              <div style={{ marginTop: 4 }}>
                <strong style={{ fontSize: 13 }}>{t('peek.culture.history')}</strong>
                {own.length === 0 ? <p style={{ ...S.hint, margin: '2px 0' }}>{t('peek.culture.noChecks')}</p> : (
                  <ol style={{ margin: '2px 0 0', paddingInlineStart: 18, fontSize: 13 }}>
                    {own.map((c) => (
                      <li key={c.check_id || `${c.plate_id}-${c.check_date}`}>
                        <Day value={c.check_date} /> · {t(`plates:plateStatus.${c.status}`)}
                        {c.colony_diameter_mm != null ? ` · ${t('peek.culture.diameter', { mm: c.colony_diameter_mm })}` : ''}
                        {c.contamination_type ? ` · ${humanize(c.contamination_type)}` : ''}
                        {c.checked_by_name ? ` · ${displayName(c.checked_by_name, t)}` : ''}
                        {c.notes ? <div style={S.hint}>{c.notes}</div> : null}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <h4 style={h4}>{t('peek.culture.lineage')}</h4>
      {graphState === 'loading' && <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />}
      {graphState === 'hidden' && <p style={S.hint}>{t('details.restricted')}</p>}
      {graphState === 'failed' && <p style={S.hint}>{t('peek.culture.lineageFailed')}</p>}
      {graphState === 'ready' && graph && (
        <div data-testid="hub-peek-graph">
          <ProvenanceGraph stages={(graph.stages || []).filter((st) => !st.removed_at)} edges={graph.edges || []}
            linked={graph.linked || {}} selectedId={null} onSelect={() => {}} canEdit={false}
            stageTitle={stageTitle} t={tSpec} />
        </div>
      )}

      <div style={headRow}>
        <h4 style={h4}>{t('peek.culture.photos')}</h4>
        {canDish && (
          <button type="button" style={S.ghostBtn} title={t('tip.quick.uploadDish')} data-testid="hub-peek-upload-dish"
            onClick={() => onAction('labPhoto', item, { plateId })}>
            {t('peek.culture.uploadDish')}
          </button>
        )}
      </div>
      {photos.length === 0 ? <p style={S.hint}>{t('peek.culture.noPhotos')}</p>
        : <PhotoGallery photos={photos} thumbWidth={96} thumbHeight={72} testId="hub-peek-dish-photos" />}

      <h4 style={h4}>{t('peek.culture.growth')}</h4>
      <GrowthPanel curves={dossier.culture_growth || []} testId="hub-peek-growth" />
      {can(user, 'lab:read') && measurable.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingInlineStart: 18, fontSize: 13 }} data-testid="hub-peek-analyse">
          {measurable.map(({ plate, photo }) => (
            <li key={plate.plate_id}>
              <Link to={`/tools/analysis?mode=culture&plate=${plate.plate_id}&photo=${photo.photo_id}`}
                    data-testid={`hub-peek-analyse-${plate.plate_id}`}>
                {t('peek.culture.analyse', { code: plate.plate_code })}
              </Link>
            </li>
          ))}
        </ul>
      )}

      <div style={headRow}>
        <h4 style={h4}>{t('peek.culture.micrographs.title')}</h4>
        {canMicro && (
          <button type="button" style={S.ghostBtn} title={t('tip.quick.uploadMicrograph')} data-testid="hub-peek-upload-micrograph"
            onClick={() => onAction('micrograph', item, { plateId })}>
            {t('peek.culture.uploadMicrograph')}
          </button>
        )}
      </div>
      <CultureMicrographs item={item} plates={plates} canType={canMicro} t={t} reloadKey={dossier} />
      {eyepiece.length > 0 && (
        <PhotoGallery photos={eyepiece} thumbWidth={96} thumbHeight={72} testId="hub-peek-eyepiece-photos" />
      )}
    </div>
  );
}

/* ── dna ─────────────────────────────────────────────────────────────────── */
// The row's own DNA tab: the extracts as recorded, their runs and answers,
// and the reference phylogeny opened on the isolate's genus.
export function DnaPeek(props) {
  return <div data-testid="hub-peek-dna"><DnaTab {...props} /></div>;
}

const h4 = { fontSize: 14, margin: '14px 0 6px' };
const headRow = { display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', justifyContent: 'space-between' };
const facts = { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '2px 12px', fontSize: 13, margin: 0 };
const dt = { color: 'var(--c-text-muted-fg)', margin: 0 };
const dd = { margin: 0 };
